/** Invoices page with list, filtering, and actions. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Refresh,
  Download,
  Email,
  MoreVert,
  Add,
  Visibility,
  Delete,
  CheckCircle,
} from "@mui/icons-material";
import { format } from "date-fns";
import { invoicesApi } from "../api/client";

interface InvoiceItem {
  id: string;
  destination_prefix: string;
  total_calls: number;
  total_minutes: number;
  rate: number;
  cost: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  customer_id: string;
  period_start: string;
  period_end: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: string;
  due_date: string;
  pdf_path: string;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<string, "default" | "success" | "warning" | "error"> = {
  draft: "default",
  sent: "default",
  paid: "success",
  partial: "warning",
  overdue: "error",
  cancelled: "default",
};

/** Invoices page component. */
const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null);
  const [generateData, setGenerateData] = useState({
    customer_id: "",
    period_start: "",
    period_end: "",
  });

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: pageSize };
      if (statusFilter) params.invoice_status = statusFilter;

      const response = await invoicesApi.list(params);
      setInvoices(response.data);
      setTotalCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [page, pageSize, statusFilter]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleDownloadPdf = async () => {
    if (!selectedInvoice) return;
    try {
      const response = await invoicesApi.downloadPdf(selectedInvoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${selectedInvoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
    handleMenuClose();
  };

  const handleSendEmail = async () => {
    if (!selectedInvoice) return;
    try {
      await invoicesApi.sendEmail(selectedInvoice.id);
      alert("Invoice email sent successfully");
    } catch (error) {
      console.error("Failed to send email:", error);
      alert("Failed to send email");
    }
    handleMenuClose();
  };

  const handleViewDetails = async () => {
    if (!selectedInvoice) return;
    try {
      const response = await invoicesApi.get(selectedInvoice.id);
      setInvoiceDetails(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch invoice details:", error);
    }
    handleMenuClose();
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice || selectedInvoice.status !== "draft") return;
    try {
      await invoicesApi.delete(selectedInvoice.id);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to delete invoice:", error);
    }
    handleMenuClose();
  };

  const handleGenerateInvoice = async () => {
    try {
      await invoicesApi.generate(generateData);
      setGenerateDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to generate invoice:", error);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Invoices
        </Typography>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setGenerateDialogOpen(true)}
          >
            Generate Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchInvoices}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.customer_id}</TableCell>
                    <TableCell>
                      {format(new Date(invoice.period_start), "MMM dd, yyyy")} -{" "}
                      {format(new Date(invoice.period_end), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell>
                      {invoice.currency} {invoice.total_amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        color={statusColors[invoice.status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(invoice.due_date), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, invoice)}
                        aria-label="Invoice actions"
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuList>
          <MenuItemComponent onClick={handleViewDetails}>
            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItemComponent>
          <MenuItemComponent onClick={handleDownloadPdf}>
            <ListItemIcon><Download fontSize="small" /></ListItemIcon>
            <ListItemText>Download PDF</ListItemText>
          </MenuItemComponent>
          <MenuItemComponent onClick={handleSendEmail}>
            <ListItemIcon><Email fontSize="small" /></ListItemIcon>
            <ListItemText>Send Email</ListItemText>
          </MenuItemComponent>
          {selectedInvoice?.status === "draft" && (
            <MenuItemComponent onClick={handleDeleteInvoice} sx={{ color: "error.main" }}>
              <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete Draft</ListItemText>
            </MenuItemComponent>
          )}
        </MenuList>
      </Menu>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent>
          {invoiceDetails && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Invoice Number
              </Typography>
              <Typography variant="body1" gutterBottom>
                {invoiceDetails.invoice_number}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                Period
              </Typography>
              <Typography variant="body1" gutterBottom>
                {format(new Date(invoiceDetails.period_start), "MMM dd, yyyy")} -{" "}
                {format(new Date(invoiceDetails.period_end), "MMM dd, yyyy")}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                Amounts
              </Typography>
              <Typography variant="body1">
                Subtotal: ${invoiceDetails.subtotal?.toFixed(2)}<br />
                Tax: ${invoiceDetails.tax_amount?.toFixed(2)}<br />
                <strong>Total: ${invoiceDetails.total_amount?.toFixed(2)}</strong>
              </Typography>

              {invoiceDetails.items && invoiceDetails.items.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                    Line Items
                  </Typography>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Destination</TableCell>
                          <TableCell align="right">Calls</TableCell>
                          <TableCell align="right">Minutes</TableCell>
                          <TableCell align="right">Rate</TableCell>
                          <TableCell align="right">Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetails.items.map((item: InvoiceItem) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.destination_prefix}</TableCell>
                            <TableCell align="right">{item.total_calls}</TableCell>
                            <TableCell align="right">{item.total_minutes}</TableCell>
                            <TableCell align="right">${item.rate.toFixed(4)}</TableCell>
                            <TableCell align="right">${item.cost.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Customer ID"
            value={generateData.customer_id}
            onChange={(e) => setGenerateData({ ...generateData, customer_id: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            type="date"
            label="Period Start"
            value={generateData.period_start}
            onChange={(e) => setGenerateData({ ...generateData, period_start: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            type="date"
            label="Period End"
            value={generateData.period_end}
            onChange={(e) => setGenerateData({ ...generateData, period_end: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGenerateInvoice} variant="contained">Generate</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Invoices;
